import React, { useEffect, useMemo, useState } from "react";
import AuthService from "network/AuthService";

const apiBaseURL = (process.env.REACT_APP_API_URL || "https://api.oekfb.eu").replace(/\/$/, "");

const isApiImage = (src) => {
  if (!src) return false;
  if (/^data:|^blob:/i.test(src)) return false;
  if (!/^https?:\/\//i.test(src)) return true;

  try {
    return new URL(src).origin === new URL(apiBaseURL).origin;
  } catch {
    return false;
  }
};

const buildImageURL = (src) => {
  if (/^https?:\/\//i.test(src)) return src;
  return `${apiBaseURL}/${String(src).replace(/^\/+/, "")}`;
};

const AuthenticatedImage = ({ src, alt, className, ...props }) => {
  const [objectURL, setObjectURL] = useState(null);
  const [failedAuthLoad, setFailedAuthLoad] = useState(false);
  const shouldFetchWithAuth = useMemo(() => isApiImage(src), [src]);

  useEffect(() => {
    if (!src || !shouldFetchWithAuth) {
      setObjectURL(null);
      setFailedAuthLoad(false);
      return undefined;
    }

    let isMounted = true;
    let nextObjectURL = null;
    const controller = new AbortController();
    const token = new AuthService().getAuthToken();

    setObjectURL(null);
    setFailedAuthLoad(false);

    fetch(buildImageURL(src), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Image request failed (${response.status})`);
        return response.blob();
      })
      .then((blob) => {
        nextObjectURL = URL.createObjectURL(blob);
        if (isMounted) setObjectURL(nextObjectURL);
      })
      .catch((error) => {
        if (error.name !== "AbortError" && isMounted) setFailedAuthLoad(true);
      });

    return () => {
      isMounted = false;
      controller.abort();
      if (nextObjectURL) URL.revokeObjectURL(nextObjectURL);
    };
  }, [src, shouldFetchWithAuth]);

  useEffect(() => () => {
    if (objectURL) URL.revokeObjectURL(objectURL);
  }, [objectURL]);

  const imageSrc = shouldFetchWithAuth && !failedAuthLoad ? objectURL || "" : src || "";

  return (
    <img
      className={className}
      src={imageSrc}
      alt={alt}
      {...props}
    />
  );
};

export default AuthenticatedImage;
